import {use, useMemo} from 'react'
import {visit} from 'unist-util-visit'
import {dataAttrToPropName, parseJsonAttribute} from '../utils'
import type {CopilotMarkdownExtension, ReactComponentsExtension} from '../extension'
import {Spinner} from '@primer/react'
import styles from './Mermaid.module.css'
import {clsx} from 'clsx'
import {ExtensionContext} from './ExtensionContext'

// Define the property names for our Mermaid diagram
const mermaidAttribute = 'data-mermaid-props'
const mermaidProperty = dataAttrToPropName(mermaidAttribute)

interface MermaidProps {
  code: string
  identity: string
  viewscreenHost: string
}

interface MermaidExtensionOptions {
  viewscreenHost: string
}

interface MermaidRendererProps extends MermaidProps {
  /**
   * Whether the source of this diagram is still being streamed in. When true, a spinner
   * is rendered instead of the enrichment markers so the viewscreen pipeline never sees
   * partial / invalid mermaid syntax. If undefined, falls back to `ExtensionContext`.
   */
  isStreaming?: boolean
}

export function MermaidRenderer({code, identity, viewscreenHost, isStreaming: isStreamingProp}: MermaidRendererProps) {
  // Create the viewscreen URL using the provided host or the default
  const host = viewscreenHost
  const viewscreenPath = '/markdown/mermaid'
  const viewscreenUrl = `${host}${viewscreenPath}`

  const {isStreaming: isStreamingContext} = use(ExtensionContext)
  const isStreaming = isStreamingProp ?? isStreamingContext

  // Base64 encode the Mermaid code for the viewscreen service
  const encodedData = useMemo(() => btoa(encodeURIComponent(code)), [code])

  // Show a helpful message when code is empty instead of a cryptic render error
  if (!code.trim()) {
    return (
      <section className={clsx(styles.diagramContainer)} aria-label="Mermaid diagram rendered output container">
        <p className={styles.emptyMessage}>No mermaid code to preview</p>
      </section>
    )
  }

  // While the response is still streaming, the mermaid code may be partial/invalid.
  // Render just a loader (without enrichment markers) so the viewscreen enrichment
  // system doesn't try to render incomplete syntax and surface an error.
  if (isStreaming) {
    return (
      <section className={clsx(styles.diagramContainer)} aria-label="Mermaid diagram loading">
        <span className={styles.loaderContainer} role="presentation">
          <Spinner />
        </span>
      </section>
    )
  }

  return (
    <section
      className={clsx('js-render-needs-enrichment', 'render-needs-enrichment', styles.diagramContainer)}
      data-identity={identity}
      data-host={host}
      data-src={`${viewscreenUrl}?data=${encodedData}`}
      data-type="mermaid"
      aria-label="Mermaid diagram rendered output container"
    >
      <div className="js-render-enrichment-target" data-json={JSON.stringify({data: code})} data-plain={code}>
        <div className={styles.hiddenSourceCode}>
          <pre lang="mermaid">{code}</pre>
        </div>
      </div>
      <span className={clsx('js-render-enrichment-loader', styles.loaderContainer)} role="presentation">
        <Spinner />
      </span>
    </section>
  )
}

const reactComponents: ReactComponentsExtension = {
  code: (props, fallthrough) => {
    const isMermaid = props.className?.includes('language-mermaid')

    if (!isMermaid) return fallthrough

    const mermaidProps = parseJsonAttribute<MermaidProps>(props, mermaidAttribute)
    if (!mermaidProps) return fallthrough

    return (
      <MermaidRenderer
        code={mermaidProps.code}
        identity={mermaidProps.identity}
        viewscreenHost={mermaidProps.viewscreenHost}
      />
    )
  },
}

export default function mermaidExtension(options: MermaidExtensionOptions): CopilotMarkdownExtension {
  return {
    transformMarkdown: tree =>
      visit(tree, 'code', node => {
        if (node.lang !== 'mermaid') return

        node.data = {
          ...node.data,
          hName: 'code',
          hProperties: {
            className: ['language-mermaid'],
            [mermaidProperty]: JSON.stringify({
              code: node.value,
              identity: `mermaid-${Math.random().toString(36).slice(2, 11)}`,
              viewscreenHost: options.viewscreenHost,
            } satisfies MermaidProps),
          },
        }
      }),
    transformHtml: tree =>
      // Code nodes render as two nested elements (pre > code) by default, so we transform the html to remove the outer element
      visit(tree, 'element', (node, i, parent) => {
        const child = node.children?.[0]
        if (
          parent &&
          i !== undefined &&
          node.tagName === 'pre' &&
          node.children?.length === 1 &&
          child?.type === 'element' &&
          child.tagName === 'code' &&
          mermaidProperty in child.properties
        )
          parent.children.splice(i, 1, child)
      }),
    reactComponents,
  }
}
